import React, { useState, useEffect } from 'react';
import Toast from './Toast';
import { API_CONFIG } from '../../config/api';
import './AISolutionsApproval.css';

interface PendingAISolution {
  id: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  company_name: string;
  category: string;
  pricing_model: string;
  features: string[];
  use_cases: string[];
  keywords: string[];
  tags: string[];
  website_url: string;
  created_at: string;
}

const AISolutionsApproval: React.FC = () => {
  const [pendingSolutions, setPendingSolutions] = useState<PendingAISolution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolution, setSelectedSolution] = useState<PendingAISolution | null>(null);
  const [editedSolution, setEditedSolution] = useState<PendingAISolution | null>(null);
  const [selectedForApproval, setSelectedForApproval] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    fetchPendingSolutions();
  }, []);

  useEffect(() => {
    // Auto-select all solutions for approval when loaded
    if (pendingSolutions.length > 0) {
      setSelectedForApproval(new Set(pendingSolutions.map(s => s.id)));
    }
  }, [pendingSolutions]);

  const fetchPendingSolutions = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/ai-solutions/pending`);
      const data = await response.json();
      
      if (data.success) {
        setPendingSolutions(data.pendingSolutions);
      }
    } catch (error) {
      console.error('Error fetching pending AI solutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSolution = (solution: PendingAISolution) => {
    setSelectedSolution(solution);
    setEditedSolution({ ...solution });
  };

  const handleToggleSelection = (solutionId: number) => {
    const newSelected = new Set(selectedForApproval);
    if (newSelected.has(solutionId)) {
      newSelected.delete(solutionId);
    } else {
      newSelected.add(solutionId);
    }
    setSelectedForApproval(newSelected);
  };

  const handleInputChange = (field: keyof PendingAISolution, value: any) => {
    if (editedSolution) {
      setEditedSolution({
        ...editedSolution,
        [field]: value
      });
    }
  };

  const handleArrayInputChange = (field: 'features' | 'use_cases' | 'keywords' | 'tags', value: string) => {
    if (editedSolution) {
      const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
      setEditedSolution({
        ...editedSolution,
        [field]: arrayValue
      });
    }
  };

  const handleSave = async () => {
    if (!editedSolution) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/ai-solutions/${editedSolution.id}/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedSolution),
      });

      const data = await response.json();
      
      if (data.success) {
        setPendingSolutions(prev => 
          prev.map(s => s.id === editedSolution.id ? editedSolution : s)
        );
        setToast({ message: 'Changes saved successfully!', type: 'success' });
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setToast({ message: 'Failed to save changes', type: 'error' });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedForApproval.size === 0) {
      setToast({ message: 'No solutions selected for approval', type: 'error' });
      return;
    }

    try {
      const selectedSolutions = pendingSolutions.filter(s => selectedForApproval.has(s.id));
      
      for (const solution of selectedSolutions) {
        await fetch(`${API_CONFIG.BASE_URL}/api/admin/ai-solutions/${solution.id}/approve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(solution),
        });
      }

      setPendingSolutions(prev => prev.filter(s => !selectedForApproval.has(s.id)));
      setSelectedForApproval(new Set());
      setSelectedSolution(null);
      setEditedSolution(null);
      setToast({ message: `${selectedSolutions.length} AI solutions approved successfully!`, type: 'success' });
    } catch (error) {
      console.error('Error approving solutions:', error);
      setToast({ message: 'Failed to approve some solutions', type: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForApproval.size === 0) {
      setToast({ message: 'No solutions selected for deletion', type: 'error' });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedForApproval.size} selected solutions? This action cannot be undone.`)) {
      return;
    }

    try {
      const selectedSolutions = pendingSolutions.filter(s => selectedForApproval.has(s.id));
      
      for (const solution of selectedSolutions) {
        await fetch(`${API_CONFIG.BASE_URL}/api/admin/ai-solutions/${solution.id}/reject`, {
          method: 'POST',
        });
      }

      setPendingSolutions(prev => prev.filter(s => !selectedForApproval.has(s.id)));
      setSelectedForApproval(new Set());
      setSelectedSolution(null);
      setEditedSolution(null);
      setToast({ message: `${selectedSolutions.length} AI solutions deleted successfully!`, type: 'success' });
    } catch (error) {
      console.error('Error deleting solutions:', error);
      setToast({ message: 'Failed to delete some solutions', type: 'error' });
    }
  };

  if (loading) {
    return <div className="ai-solutions-approval__loading">Loading pending AI solutions...</div>;
  }

  return (
    <div className="ai-solutions-approval">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <h2>AI Solutions Approval</h2>
      
      <div className="ai-solutions-approval__content">
        <div className="ai-solutions-approval__list">
          <div className="list-header">
            <h3>Pending Solutions ({pendingSolutions.length})</h3>
            <div className="bulk-actions">
              <button 
                className="approve-selected-btn"
                onClick={handleBulkApprove}
                disabled={selectedForApproval.size === 0}
              >
                Approve Selected ({selectedForApproval.size})
              </button>
              <button 
                className="delete-selected-btn"
                onClick={handleBulkDelete}
                disabled={selectedForApproval.size === 0}
              >
                Delete Selected
              </button>
            </div>
          </div>
          
          {pendingSolutions.length === 0 ? (
            <p>No pending AI solutions to review.</p>
          ) : (
            <div className="pending-solutions-list">
              {pendingSolutions.map((solution) => (
                <div
                  key={solution.id}
                  className={`solution-item ${selectedSolution?.id === solution.id ? 'selected' : ''}`}
                >
                  <div className="solution-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedForApproval.has(solution.id)}
                      onChange={() => handleToggleSelection(solution.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div 
                    className="solution-content"
                    onClick={() => handleSelectSolution(solution)}
                  >
                    <h4>{solution.name}</h4>
                    <p className="company">{solution.company_name}</p>
                    <p className="category">{solution.category}</p>
                    <p className="date">Discovered: {new Date(solution.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedSolution && editedSolution && (
          <div className="ai-solutions-approval__editor">
            <h3>Edit Solution Details</h3>
            
            <div className="editor-form">
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={editedSolution.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Company:</label>
                <input
                  type="text"
                  value={editedSolution.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Category:</label>
                <select
                  value={editedSolution.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="Machine Learning">Machine Learning</option>
                  <option value="NLP">Natural Language Processing</option>
                  <option value="Computer Vision">Computer Vision</option>
                  <option value="Data Analytics">Data Analytics</option>
                  <option value="Automation">Automation</option>
                  <option value="Development Tools">Development Tools</option>
                  <option value="Business Intelligence">Business Intelligence</option>
                  <option value="Content Generation">Content Generation</option>
                </select>
              </div>

              <div className="form-group">
                <label>Pricing Model:</label>
                <select
                  value={editedSolution.pricing_model}
                  onChange={(e) => handleInputChange('pricing_model', e.target.value)}
                >
                  <option value="free">Free</option>
                  <option value="freemium">Freemium</option>
                  <option value="paid">Paid</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div className="form-group">
                <label>Website URL:</label>
                <input
                  type="url"
                  value={editedSolution.website_url}
                  onChange={(e) => handleInputChange('website_url', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Short Description:</label>
                <textarea
                  value={editedSolution.short_description}
                  onChange={(e) => handleInputChange('short_description', e.target.value)}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>Full Description:</label>
                <textarea
                  value={editedSolution.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Features (comma-separated):</label>
                <textarea
                  value={editedSolution.features?.join(', ') || ''}
                  onChange={(e) => handleArrayInputChange('features', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Use Cases (comma-separated):</label>
                <textarea
                  value={editedSolution.use_cases?.join(', ') || ''}
                  onChange={(e) => handleArrayInputChange('use_cases', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Keywords (comma-separated):</label>
                <input
                  type="text"
                  value={editedSolution.keywords?.join(', ') || ''}
                  onChange={(e) => handleArrayInputChange('keywords', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Tags (comma-separated):</label>
                <input
                  type="text"
                  value={editedSolution.tags?.join(', ') || ''}
                  onChange={(e) => handleArrayInputChange('tags', e.target.value)}
                />
              </div>

              <div className="editor-actions">
                <button className="save-btn" onClick={handleSave}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISolutionsApproval;
